import { ToastError, ToastInfo } from "components/Global/Toasts/Toasty";
import { takeLatest } from "redux-saga/effects";
import { isEmpty } from "lodash";
import { defaultReturn, generateDefaultActions, makePayload, makeWritePayload, requestSaga } from "shared-utility/store/utils/helpers";

// ACTIONS
export const WIZARD_STEP_INCREMENT = generateDefaultActions("WIZARD_STEP_INCREMENT");
export const wizardStepIncrement = () => makePayload(WIZARD_STEP_INCREMENT.value);

export const WIZARD_STEP_DECREMENT = generateDefaultActions("WIZARD_STEP_DECREMENT");
export const wizardStepDecrement = () => makePayload(WIZARD_STEP_DECREMENT.value);

export const SET_SELECTED_REPORT_CONFIG = generateDefaultActions("SET_SELECTED_REPORT_CONFIG");
export const setSelectedReportConfig = payload => makeWritePayload(SET_SELECTED_REPORT_CONFIG.value, payload);

export const SET_SELECTIONS = generateDefaultActions("SET_SELECTIONS");
export const setSelections = payload => makeWritePayload(SET_SELECTIONS.value, payload);

export const SET_REPORT_CONFIG = generateDefaultActions("SET_REPORT_CONFIG");
export const setReportConfig = payload => makeWritePayload(SET_REPORT_CONFIG.value, payload);

export const INITIALIZE_REPORT_CONFIG = generateDefaultActions("INITIALIZE_REPORT_CONFIG");
export const initializeReportConfig = payload => makeWritePayload(INITIALIZE_REPORT_CONFIG.value, payload);

export const INITIALIZE_STATE = generateDefaultActions("INITIALIZE_STATE");
export const initializeState = payload => makeWritePayload(INITIALIZE_STATE.value, payload);

export const SET_REPORTING_SETUP_LOADING = generateDefaultActions("SET_REPORTING_SETUP_LOADING");
export const setReportingSetupLoading = () => makeWritePayload(SET_REPORTING_SETUP_LOADING.value);

export const PUT_LOAN_DELIVERABLE = generateDefaultActions("PUT_LOAN_DELIVERABLE");

export const POST_REPORT_CONFIG = generateDefaultActions("POST_REPORT_CONFIG");
export const postReportConfig = payload => makePayload(POST_REPORT_CONFIG.value, payload);
function* postReportConfigSaga({ data }) {
  const selectedDeliverableId = data.selectedDeliverableId;
  delete data.selectedDeliverableId;
  const url = "/report-groups";
  yield requestSaga(POST_REPORT_CONFIG, url, "POST", data, null, null, true);
  if (data.id !== "new" && selectedDeliverableId) {
    const url = "/lrd/deliverables";
    yield requestSaga(PUT_LOAN_DELIVERABLE, url, "PUT", { reportGroupId: data.id, deliverableId: selectedDeliverableId });
  }
}

export const PUT_REPORT_CONFIG = generateDefaultActions("PUT_REPORT_CONFIG");
export const putReportConfig = payload => makePayload(PUT_REPORT_CONFIG.value, payload);
function* putReportConfigSaga({ data }) {
  const url = "/report-groups";
  yield requestSaga(PUT_REPORT_CONFIG, url, "PUT", data, null, null, true);
}

export const PUT_PROJECTED_REPORT_CONFIG = generateDefaultActions("PUT_PROJECTED_REPORT_CONFIG");
export const putProjectedReportConfig = payload => makePayload(PUT_PROJECTED_REPORT_CONFIG.value, payload);
function* putProjectedReportConfigSaga({ data }) {
  const url = "/report-groups/projected";
  yield requestSaga(PUT_PROJECTED_REPORT_CONFIG, url, "PUT", data, null, null, true);
}

export const GET_REPORT_CONFIG = generateDefaultActions("GET_REPORT_CONFIG");
export const getReportConfig = payload => makePayload(GET_REPORT_CONFIG.value, payload);
function* getReportConfigSaga({ id }) {
  const url = `/report-groups/${id}`;
  yield requestSaga(GET_REPORT_CONFIG, url, "GET", null, null, null, true);
}

export const GET_NEW_REPORT_CONFIG = generateDefaultActions("GET_NEW_REPORT_CONFIG");
export const getNewReportConfig = payload => makePayload(GET_NEW_REPORT_CONFIG.value, payload);
function* getNewReportConfigSaga() {
  const url = "/lrd/getNewReportGroupData";
  yield requestSaga(GET_NEW_REPORT_CONFIG, url, "GET");
}

export const GET_REPORT_CONFIG_BY_REPORTID = generateDefaultActions("GET_REPORT_CONFIG_BY_REPORTID");
export const getReportConfigByReportId = payload => makePayload(GET_REPORT_CONFIG_BY_REPORTID.value, payload);
function* getReportConfigByReportIdSaga({ reportId }) {
  const url = `/lrd/getReportConfigByReportId/${reportId}`;
  yield requestSaga(GET_REPORT_CONFIG_BY_REPORTID, url, "GET");
}

export const actionCreators = {
  getReportConfig,
  initializeReportConfig,
  initializeState,
  postReportConfig,
  putReportConfig,
  putProjectedReportConfig,
  setReportConfig,
  setSelectedReportConfig,
  setReportingSetupLoading,
  setSelections,
  wizardStepDecrement,
  wizardStepIncrement,
  getReportConfigByReportId
};

// REDUCER
const defaultReportConfig = {
  dataAsOfDate: null,
  debtYieldLastUpdatedDate: null,
  dscrLastUpdatedDate: null,
  debtYieldProjectedLastUpdatedDate: null,
  dscrProjectedLastUpdatedDate: null,
  firstReportDueDate: null,
  frequency: null,
  lastReportDueDate: null,
  noiLastUpdatedDate: null,
  noiProjectedUpdatedDate: null,
  shouldCalculateDebtYield: false,
  shouldCalculateDscr: false
};

const initialState = {
  currentUserId: null,
  dealId: null,
  isDeleteDialogOpen: false,
  isEditing: false,
  isLoading: false,
  newReportConfig: [],
  reportConfig: null,
  reportSelections: { selectedDealId: null, selectedLoanId: null, selectedDeliverableId: null, reportName: "", deliverable: null },
  selectedReportId: null,
  reportName: "",
  validationError: { asOfDate: null, lastDueDate: null },
  wizardStep: 0
};

export function lenderReportingSetupReducer(state = initialState, action) {
  const { type, payload } = action;

  switch (type) {
    case GET_NEW_REPORT_CONFIG.success:
      return defaultReturn(state, payload, "newReportConfig");
    case GET_REPORT_CONFIG_BY_REPORTID.success:
      if (isEmpty(payload.reportSelections)) {
        return state;
      }

      return defaultReturn(state, payload, "reportSelections");
    case GET_REPORT_CONFIG.failure:
      ToastError(payload.message);

      return state;

    case SET_REPORTING_SETUP_LOADING.value:
      return defaultReturn({ ...state, isLoading: Boolean(payload) });
    case SET_REPORT_CONFIG.value:
      return defaultReturn({ ...state, reportConfig: { ...state.reportConfig, ...payload } });
    case SET_SELECTED_REPORT_CONFIG.value:
      if (isEmpty(payload.reportSelections)) {
        return state;
      }

      return {
        ...state,
        reportSelections: payload.reportSelections,
        reportConfig: {
          ...state.reportConfig,
          firstReportDueDate: payload.reportSelections.reportingStartDate,
          lastReportDueDate: payload.reportSelections.reportingEndDate
        }
      };
    case SET_SELECTIONS.value:
      return defaultReturn({ ...state, ...payload });
    case INITIALIZE_REPORT_CONFIG.value:
      return defaultReturn(
        state,
        { ...defaultReportConfig, dealId: state.selectedDealId, id: state.selectedReportId, createdByUserId: state.currentUserId },
        "reportConfig"
      );
    case INITIALIZE_STATE.value:
      return defaultReturn({ ...initialState });
    case WIZARD_STEP_INCREMENT.value:
      return defaultReturn({ ...state, wizardStep: state.wizardStep + 1 });
    case WIZARD_STEP_DECREMENT.value:
      return defaultReturn({ ...state, wizardStep: state.wizardStep - 1 });
    case GET_REPORT_CONFIG.failure:
      ToastInfo("Could not fetch existing configuration");

      return defaultReturn(
        state,
        { ...defaultReportConfig, dealId: state.selectedDealId, id: state.selectedReportId, createdByUserId: state.currentUserId },
        "reportConfig"
      );
    case GET_REPORT_CONFIG.success:
      return defaultReturn({ ...state, isLoading: false }, payload.data, "reportConfig");
    case GET_REPORT_CONFIG.value:
      return defaultReturn({ ...state, isLoading: true });
    case POST_REPORT_CONFIG.value:
      return defaultReturn({ ...state, isLoading: true });
    case POST_REPORT_CONFIG.failure:
      ToastError("Could not save configuration.");

      return defaultReturn({ ...state, isLoading: false });
    case POST_REPORT_CONFIG.success:
      return defaultReturn({ ...state, isLoading: false }, payload.data, "reportConfig");
    case PUT_REPORT_CONFIG.value:
      return defaultReturn({ ...state, isLoading: true });
    case PUT_REPORT_CONFIG.failure:
      ToastError("Could not update configuration.");

      return defaultReturn({ ...state, isLoading: false });
    case PUT_REPORT_CONFIG.success:
      return defaultReturn({ ...state, isLoading: false }, payload.data, "reportConfig");

      case PUT_PROJECTED_REPORT_CONFIG.value:
        return defaultReturn({ ...state, isLoading: true });
      case PUT_PROJECTED_REPORT_CONFIG.failure:
        ToastError("Could not update projected configuration.");
  
        return defaultReturn({ ...state, isLoading: false });
      case PUT_PROJECTED_REPORT_CONFIG.success:
        return defaultReturn({ ...state, isLoading: false }, payload.data, "reportConfig");
    default:
      return state;
  }
}

// SAGA

export function* lenderReportingSetupSaga() {
  yield takeLatest(GET_REPORT_CONFIG.value, getReportConfigSaga);
  yield takeLatest(POST_REPORT_CONFIG.value, postReportConfigSaga);
  yield takeLatest(PUT_REPORT_CONFIG.value, putReportConfigSaga);
  yield takeLatest(PUT_PROJECTED_REPORT_CONFIG.value, putProjectedReportConfigSaga);
  yield takeLatest(GET_NEW_REPORT_CONFIG.value, getNewReportConfigSaga);
  yield takeLatest(GET_REPORT_CONFIG_BY_REPORTID.value, getReportConfigByReportIdSaga);
}
